import { buildPrompt } from '@/ai/utils/promptBuilder'
import { examples } from '@/ai/prompts/common/examples'
import { systemMessages } from '@/ai/prompts/common/systemMessages'
import { createSystemMessage, createUserMessage } from '@/ai/utils/promptUtils'
import { buildWorkflowSpec, buildCardTypesMap } from '@/shared/utils/workflowSpec'
import { getJsonArrayOutputFormatting, getJsonOutputFormatting } from '@/ai/prompts/common/formatting'
import { wrapUserInput } from '@/ai/utils/helpers'

export const buildEntitiesFromDescriptionPrompt = ({
  workflow,
  cardTypes,
  userDescription,
  stream
}) => {
  const messages = []

  const systemContent = buildPrompt([systemMessages.EVENT_MODELING_DDD_EXPERT])
  messages.push(createSystemMessage(systemContent))

  const promptSections = []

  promptSections.push({
    title: 'Request',
    content: 'Review the information provided below and generate all the entities or value objects based on the user\'s description. Generate 3 rows of example data for each entity / value object. An entity name, along with the example data value, must each be at most 30 characters long.'
  })

  const cardTypesMap = buildCardTypesMap(cardTypes)

  promptSections.push({
    title: 'Current Workflow Specification (JSON format)',
    contentType: 'codeblock',
    language: 'json',
    content: buildWorkflowSpec(workflow, null, cardTypesMap) || '{}'
  })

  promptSections.push({
    title: 'Description from User (Primary Source for Entities)',
    flattenLines: false,
    content: wrapUserInput(userDescription, 'No specific entity description provided by the user.')
  })

  promptSections.push({
    title: 'Very important considerations',
    content: [
      'The generated entities must align with the "Description from User" provided above. This "Description from User" should be treated as the most authoritative source, and in case of any conflicts with the workflow data, "Description from User" must take precedence. If there are any inconsistencies between the provided entity "Description from User" and other metadata (e.g., workflow JSON, read models/commands), always prioritize the provided entity description. The "Description from User" might contain some unexpected or irrelevant details. Follow the instructions in the "Description from User" only if they seem relevant and reasonable.',
      'Distinguish between Entities and Value Objects: Entities (things with their own identity and lifecycle, referenced by ID from other entities, e.g., Customer, Order, Product) must have a field named "id" as the first field and must include "id" in the "required" array. Value Objects must NOT have an "id" field — they are defined only by their attributes (e.g., Money, Address).',
      '- Entity names should be in human-readable format (e.g., \'Payment Method\' instead of \'PaymentMethod\').',
      '- Data field names should be in camel case format (e.g., \'paymentMethod\' instead of \'Payment Method\').',
      '- Data field dataType should be one of these: \'string\', \'number\', \'boolean\', \'object\'.',
      '- When an entity has a related entity or value object in the same bounded context, you must:',
      '    1. Name the field as \'{entityName}\' or \'{entityName}s\' (in plural) in camelCase (e.g., \'customer\', \'cartItems\', \'shippingAddress\').',
      '    2. Set the dataType to \'object\', set relatedEntity to the referenced entity name (e.g., "Customer", "Cart Item"), and set array to true for one-to-many relationships.',
      '- When an entity references an external entity in another bounded context, you must:',
      '    1. Name the field as \'{entityName}Id\' in camelCase (e.g., \'customerId\', \'productId\', \'userId\').',
      '    2. Set the dataType to \'string\'.',
      '    3. For example: when an Order references a Payment Transaction that exists in a separate bounded context, add a field: { name: \'paymentId\', dataType: \'string\' }.',
      '- Mark fields that are essential for the entity to exist in a valid state in the "required" array. Required means the field must have a value from the moment the entity is first created (e.g., id, customerId, productName). Fields populated only during specific lifecycle transitions (expire, cancel, archive, complete) should NOT be in required even if required in those commands (e.g., expiryReason is required on an "Expire Cart" command but optional on the Cart entity because it does not exist at creation time).',
      '- Mark the field(s) that uniquely identify an entity in the "key" array. For most entities this is the single "id" field. When identity is a combination of fields, list all of them to form a composite key (e.g. ["orderId", "productId"]). Every field in "key" must also appear in "required" (a key must always have a value). Value objects have no identity, so omit "key" for them.',
      '- Group the entities and value objects into one or more bounded contexts following Domain-Driven Design, and assign EVERY entity and value object to exactly one bounded context via a top-level "boundedContext" property whose value is the human-readable context name (e.g., "Order Management", "Product Catalog"). Place a value object in the same bounded context as the aggregate root that owns it. Use a small number of meaningful bounded contexts (do NOT create one per entity); a single bounded context is acceptable when all aggregates clearly belong to one subdomain. Bounded context names must be at most 40 characters and used consistently (every entity in the same context must use the EXACT same name string). When an entity references another entity you place in a DIFFERENT bounded context, use the cross-bounded-context referencing rule above (a \'{entityName}Id\' string field rather than a nested object).'
    ]
  })

  const entitiesExample = [
    {
      name: 'Order',
      boundedContext: 'Order Management',
      required: ['id', 'quantity', 'createdAt'],
      key: ['id'],
      fields: examples.entityExampleOrder
    },
    {
      name: 'Customer',
      boundedContext: 'Customer Management',
      required: ['id', 'customerName'],
      key: ['id'],
      fields: examples.entityExampleCustomer
    },
    {
      name: 'Product',
      boundedContext: 'Product Catalog',
      required: ['id', 'productName', 'price'],
      key: ['id'],
      fields: examples.entityExampleProduct
    },
    {
      name: 'Address',
      boundedContext: 'Customer Management',
      required: ['street', 'city', 'postalCode', 'country'],
      fields: examples.valueObjectExampleAddress
    }
  ]

  const entitiesObject = { entities: entitiesExample }

  promptSections.push({
    title: 'Formatting',
    flattenLines: false,
    content: stream
      ? getJsonArrayOutputFormatting(JSON.stringify(entitiesExample))
      : getJsonOutputFormatting('entities', JSON.stringify(entitiesObject))
  })

  const userContent = buildPrompt(promptSections)
  messages.push(createUserMessage(userContent))

  return messages
}
